import { useCallback, useEffect, useRef, useState } from 'react';
import useTooltipsEnabled from './useTooltipsEnabled';

export const SELF_HELP_HOVER_QUERY = '(hover: hover) and (pointer: fine)';

// The same input, focus, keyboard and dismissal rules for every Self-Help surface.
export default function useSelfHelp(userId) {
    const { tooltipsEnabled: enabled } = useTooltipsEnabled(userId);
    const [open, setOpen] = useState(false);
    const [canHover, setCanHover] = useState(() => Boolean(window.matchMedia?.(SELF_HELP_HOVER_QUERY).matches));
    const root = useRef(null), trigger = useRef(null), panel = useRef(null);
    const pointerFocus = useRef(false), restoringFocus = useRef(false);
    const hide = useCallback(() => setOpen(false), []);
    const restoreFocus = useCallback(() => {
        restoringFocus.current = true;
        trigger.current?.focus();
        restoringFocus.current = false;
    }, []);
    const dismiss = useCallback(() => {
        setOpen(false);
        restoreFocus();
    }, [restoreFocus]);
    useEffect(() => {
        const media = window.matchMedia?.(SELF_HELP_HOVER_QUERY);
        const change = () => { setCanHover(Boolean(media?.matches)); hide(); };
        media?.addEventListener?.('change', change);
        return () => media?.removeEventListener?.('change', change);
    }, [hide]);
    useEffect(() => { if (!enabled) hide(); }, [enabled, hide]);
    useEffect(() => {
        if (!open || !enabled) return;
        const outside = event => { if (!root.current?.contains(event.target)) hide(); };
        const escape = event => { if (event.key === 'Escape') { event.preventDefault(); dismiss(); } };
        document.addEventListener('pointerdown', outside);
        document.addEventListener('keydown', escape);
        return () => { document.removeEventListener('pointerdown', outside); document.removeEventListener('keydown', escape); };
    }, [open, enabled, hide, dismiss]);
    return {
        enabled, open: enabled && open, canHover, root, trigger, panel, hide, dismiss, restoreFocus,
        rootProps: {
            onPointerEnter: event => { if (canHover && event.pointerType !== 'touch') setOpen(true); },
            onPointerLeave: event => { if (canHover && event.pointerType !== 'touch') hide(); },
            onBlur: event => { if (!event.currentTarget.contains(event.relatedTarget)) hide(); },
        },
        triggerProps: {
            onPointerDown: () => { pointerFocus.current = true; },
            onPointerCancel: () => { pointerFocus.current = false; },
            // Touch browsers can focus after pointerup, just before click.
            // Keep suppressing that focus so the following click opens once.
            onFocus: () => { if (!pointerFocus.current && !restoringFocus.current) setOpen(true); },
            onClick: () => { pointerFocus.current = false; setOpen(value => !value); },
            onKeyDown: event => {
                pointerFocus.current = false;
                if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setOpen(value => !value); }
                else if (event.key === 'Escape') { event.preventDefault(); dismiss(); }
            },
        },
    };
}
